import { extractTimestampFromUUID7, isValidUUID7 } from "@/utils/uuid7";
import type { EncryptedMessage } from "@/server/types/umbra/message";
import {
  umbra_getMessages,
  umbra_getComments,
} from "@/server/services/umbra/messageService";
import {
  STRANDED_CONFIRM_PAGE_SIZE,
  STRANDED_CONFIRM_MAX_PAGES,
  STRANDED_CONFIRM_SKEW_MARGIN_MS,
  STRANDED_CONFIRM_REQUEST_TIMEOUT_MS,
} from "@/config/constants";

/**
 * The maximum possible UUID7 (all-max timestamp, version 7, top variant). Used as
 * the `before` anchor for the FIRST page so the newest-first read starts above
 * EVERY real message id — including one whose server-assigned id sorts above the
 * client's "now" because the server clock runs ahead. Anchoring on a client-clock
 * "now" would silently exclude such a just-delivered message and risk confirming
 * it "absent". This anchor is clock-independent.
 */
export const MAX_UUID7 = "ffffffff-ffff-7fff-bfff-ffffffffffff";

/** A stranded PENDING send to confirm against the server. */
export type StrandedCandidate = {
  _clientId: string;
  chatId: string;
  threadId: string | null;
};

/**
 * The server's verdict for one candidate, keyed back by `_clientId`:
 * - `found`      – its `client_id` was echoed in the fetched window; the message
 *                  IS on the server. `serverId` is the real server id.
 * - `not_found`  – its `client_id` was absent AND the fetched window covers where
 *                  it would be (paged past its send time − skew, or the whole
 *                  chat/thread) → a genuine send failure.
 * - `unconfirmed`– we could not build a conclusive window (hit the page cap
 *                  before covering its send time, or the fetch threw / offline).
 */
export type ConfirmVerdict =
  | { kind: "found"; serverId: string }
  | { kind: "not_found" }
  | { kind: "unconfirmed" };

type MessagesPage = { messages: EncryptedMessage[]; cursor: string | null };

/** Injectable transport so the lookup is unit-testable without a network. */
export type ConfirmDeps = {
  getMessages: (args: {
    chatId: string;
    before: string;
    pageSize: number;
  }) => Promise<MessagesPage>;
  getComments: (args: {
    chatId: string;
    threadId: string;
    before: string;
    pageSize: number;
  }) => Promise<MessagesPage>;
};

/**
 * Bound each lookup with an abort timeout — the shared axios client sets no
 * `timeout`, so a hung connection would otherwise stall the whole boot sweep.
 */
const withTimeout = <T>(
  fn: (signal: AbortSignal) => Promise<T>,
): Promise<T> => {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    STRANDED_CONFIRM_REQUEST_TIMEOUT_MS,
  );
  return fn(controller.signal).finally(() => clearTimeout(timer));
};

const defaultDeps: ConfirmDeps = {
  getMessages: ({ chatId, before, pageSize }) =>
    withTimeout((signal) =>
      umbra_getMessages({ chatId, before, pageSize, signal }),
    ),
  getComments: ({ chatId, threadId, before, pageSize }) =>
    withTimeout((signal) =>
      umbra_getComments({ chatId, threadId, before, pageSize, signal }),
    ),
};

/**
 * UUID7 → epoch ms, or NaN for a non-UUID7 id. `extractTimestampFromUUID7`
 * substitutes a fixed 2020 fallback for an invalid id, which would silently read
 * as "very old" and drive a false `not_found`; guard so it reads as unknown.
 */
const idToMs = (id: string): number =>
  isValidUUID7(id) ? Date.parse(extractTimestampFromUUID7(id)) : NaN;

/**
 * Confirm a batch of stranded sends against the server by `client_id` (#1177).
 *
 * Replaces the `id === _clientId` guess with a fact: the boot reconciliation
 * cannot tell "the server never got it" from "the server got it but the response
 * died" — both leave `id === _clientId`. This asks the server which one it is.
 *
 * Design:
 * - **Grouped by (chatId, threadId).** A chat-level read does not surface thread
 *   comments (category COMMENT), so threaded rows are looked up via the comments
 *   endpoint. `MessageEntity` already carries `threadId`; no new data is needed.
 * - **Clock-independent window.** Each group is paged NEWEST-FIRST starting from a
 *   `MAX_UUID7` `before` anchor (not the client clock, not `_clientId`). This
 *   guarantees a delivered message is in the window regardless of client/server
 *   clock skew, so it is matched (`found`) rather than mistaken for absent.
 * - **Coverage-based `not_found`.** Absence is a REAL failure only once the fetched
 *   window has covered where the message would be: either the whole chat/thread is
 *   exhausted, or we paged back past the send's own creation time (`_clientId`
 *   timestamp) minus a skew margin. Because a stranded send is recent, that is a
 *   few pages even in a huge chat. Anything short of that — the page cap, an error,
 *   offline — is `unconfirmed`, whose safe handling never deletes or re-sends.
 *
 * Never decrypts (callers already hold decrypted content) and never writes to the
 * DB — it returns verdicts keyed by `_clientId` for the caller to apply under its
 * own transactional re-read guard.
 */
export async function confirmStrandedSends(
  candidates: StrandedCandidate[],
  deps: ConfirmDeps = defaultDeps,
): Promise<Map<string, ConfirmVerdict>> {
  const verdicts = new Map<string, ConfirmVerdict>();
  if (candidates.length === 0) {
    return verdicts;
  }

  // Group by (chatId, threadId). Ids are UUIDs (no spaces), so a space is a safe
  // join separator for the composite key.
  const groups = new Map<string, StrandedCandidate[]>();
  for (const c of candidates) {
    const key = `${c.chatId} ${c.threadId ?? ""}`;
    const arr = groups.get(key);
    if (arr) {
      arr.push(c);
    } else {
      groups.set(key, [c]);
    }
  }

  for (const group of groups.values()) {
    const { chatId, threadId } = group[0];
    // Candidates still awaiting a match in this group, keyed by client_id.
    const remaining = new Map(group.map((c) => [c._clientId, c] as const));
    let before = MAX_UUID7;
    let oldestFetchedId: string | null = null;
    let exhausted = false;

    try {
      for (
        let page = 0;
        page < STRANDED_CONFIRM_MAX_PAGES && remaining.size > 0;
        page++
      ) {
        const res = threadId
          ? await deps.getComments({
              chatId,
              threadId,
              before,
              pageSize: STRANDED_CONFIRM_PAGE_SIZE,
            })
          : await deps.getMessages({
              chatId,
              before,
              pageSize: STRANDED_CONFIRM_PAGE_SIZE,
            });

        const messages = res.messages ?? [];
        for (const m of messages) {
          if (m.client_id && remaining.has(m.client_id)) {
            verdicts.set(m.client_id, { kind: "found", serverId: m.id });
            remaining.delete(m.client_id);
          }
        }
        // Newest-first: the last item is the oldest fetched so far.
        if (messages.length > 0) {
          oldestFetchedId = messages[messages.length - 1].id;
        }

        // The server's `cursor` is the authoritative exhaustion signal — it may
        // clamp pageSize below ours, so a "short" page is NOT proof of the end.
        // An empty page also ends it.
        if (messages.length === 0 || res.cursor == null) {
          exhausted = true;
          break;
        }
        before = oldestFetchedId ?? before; // page older (oldestFetchedId is set)
      }

      // Classify whatever is still unmatched. `not_found` only when the window
      // provably contained it: the whole chat is exhausted, OR we paged back past
      // this send's creation time (minus skew). Otherwise inconclusive.
      const oldestMs = oldestFetchedId ? idToMs(oldestFetchedId) : NaN;
      for (const c of remaining.values()) {
        const candidateMs = idToMs(c._clientId);
        const coveredPastSend =
          !Number.isNaN(oldestMs) &&
          !Number.isNaN(candidateMs) &&
          oldestMs < candidateMs - STRANDED_CONFIRM_SKEW_MARGIN_MS;
        verdicts.set(
          c._clientId,
          exhausted || coveredPastSend
            ? { kind: "not_found" }
            : { kind: "unconfirmed" },
        );
      }
    } catch {
      // Any transport error → the whole group is inconclusive (never destructive).
      for (const c of remaining.values()) {
        verdicts.set(c._clientId, { kind: "unconfirmed" });
      }
    }
  }

  return verdicts;
}
